import type { Asset } from '@schibsted-svp/svp-api-types';
import type { Control, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { isAudio, isLive, isLivingThumbnailAvailable, wasLive } from 'models/asset';
import { isReady } from 'store/video/selectors';
import { uploadVideoPoster } from 'store/video/upload/actions';
import { isVideoPosterUploading } from 'store/video/upload/selectors';

import SuggestedThumbnails from 'components/video/VideoPoster/SuggestedThumbnails';
import VideoFrameForm from 'components/video/VideoPoster/VideoFrameForm';
import config from 'config';
import type { FormValues, ImageType } from '../types';
import { LivingThumbnail } from './LivingThumbnail';
import { PosterManager } from './PosterManager';
import { useFetchThumbnails, useCategoryChange } from './hooks/poster';

import css from './Poster.module.scss';

type PosterProps = {
    asset: Asset;
    control: Control<FormValues>;
    register: UseFormRegister<FormValues>;
    setValue: UseFormSetValue<FormValues>;
};

export function Poster({ asset, control, register, setValue }: PosterProps) {
    const { provider, id } = asset;

    const dispatch = useDispatch();

    const uploading = useSelector((state) => isVideoPosterUploading(state, { provider, id, type: 'main' }));
    const ready = useSelector((state) => isReady(state, { id, provider }));

    const { thumbnails, areThumbnailsLoading } = useFetchThumbnails({ asset });

    const onVideoPosterUpload = (file: File, imageType: ImageType = 'main') => {
        dispatch(uploadVideoPoster(provider, id, file, imageType));
    };
    const {
        images: { imageTypes },
    } = config.defaultNewsroomConfig;

    useCategoryChange({ asset, control, imageTypes, setValue });

    return (
        <section className={css.wrapper} data-testid="imagePoster">
            {!isAudio(asset) && (
                <div className={css.greybox}>
                    <div className={css.frameSelector}>
                        <VideoFrameForm
                            disabled={uploading || !ready}
                            isLive={isLive(asset)}
                            onVideoPosterUpload={onVideoPosterUpload}
                        />
                    </div>
                    <div className={css.suggestions}>
                        {isLivingThumbnailAvailable(asset) ? (
                            <LivingThumbnail wasLive={wasLive(asset)} register={register} />
                        ) : (
                            <SuggestedThumbnails id={id} thumbnails={thumbnails} loading={areThumbnailsLoading} />
                        )}
                    </div>
                    <p className={css.info}>*this feature is applicable to the main image only</p>
                </div>
            )}
            <div className={css.posters}>
                {imageTypes.map((type) => (
                    <PosterManager
                        key={type}
                        id={id}
                        provider={provider}
                        type={type}
                        control={control}
                        ready={ready}
                        onVideoPosterUpload={onVideoPosterUpload}
                        enableCrop
                    />
                ))}
            </div>
        </section>
    );
}
